import type { WalaSignature } from '../planningView/widgets/AssignmentList/ics204SignatureSlots';
import {
  parseTaskSignatures,
  TASK_APPROVE_EXECUTE_BD_ROLE,
} from './taskSignatureSlots';

export type TaskApprovalVersionRow = {
  id: string;
  version?: number | null;
  timestamp?: string | Date | null;
  signatures?: unknown;
};

export type TaskExecutionApprovalDrift = {
  approverName: string;
  approvedAt: number;
  approvedVersionId: string;
};

function findApproveExecutionStamp(
  signatures: WalaSignature[]
): WalaSignature | null {
  const byRole = signatures.find(
    (stamp) => stamp.role === TASK_APPROVE_EXECUTE_BD_ROLE
  );
  if (byRole) return byRole;
  // Append-only slots: Branch Director Approve Execution is index 2 once present.
  return signatures.length >= 3 ? signatures[2] ?? null : null;
}

function hasMatchingApproveStamp(
  signatures: WalaSignature[],
  stamp: WalaSignature
): boolean {
  return signatures.some(
    (entry) =>
      entry.role === stamp.role && entry.signedAt === stamp.signedAt
  );
}

/**
 * When Execution is approved and a later tip edit advanced the version id past
 * the snapshot that first carried that Branch Director Approve Execution stamp.
 */
export function getTaskExecutionApprovalDrift(
  tip: TaskApprovalVersionRow | null | undefined,
  versions: TaskApprovalVersionRow[]
): TaskExecutionApprovalDrift | null {
  if (!tip?.id) return null;
  const tipSigs = parseTaskSignatures(tip.signatures);
  const approveStamp = findApproveExecutionStamp(tipSigs);
  if (!approveStamp) return null;

  const oldestFirst = [...versions].sort((a, b) => {
    const av = Number(a.version ?? 0);
    const bv = Number(b.version ?? 0);
    if (av !== bv) return av - bv;
    const at = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const bt = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return at - bt;
  });

  const approvedVersion = oldestFirst.find((row) =>
    hasMatchingApproveStamp(parseTaskSignatures(row.signatures), approveStamp)
  );
  if (!approvedVersion?.id || approvedVersion.id === tip.id) return null;

  return {
    approverName: approveStamp.name,
    approvedAt: approveStamp.signedAt,
    approvedVersionId: approvedVersion.id,
  };
}
